import { Alert, Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconAlertCircle, IconMailCheck } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import { requestPasswordReset } from './api';
import { AuthLayout } from './AuthLayout';

/**
 * Запрос ссылки восстановления.
 *
 * Экран намеренно не сообщает, нашёлся ли такой человек. «Такого адреса нет»
 * превратило бы форму в способ проверять, кто у платформы зарегистрирован, —
 * а список клиентов Дарьи не публичный. Поэтому ответ всегда один: письмо
 * отправлено, если адрес нам знаком.
 *
 * Формулировка выбрана так, чтобы человек с опечаткой в адресе не ждал письма
 * вечно, но и не получил подтверждения чужой регистрации.
 */
export function ForgotPasswordPage() {
  const form = useForm<ForgotPasswordInput>({
    initialValues: { email: '' },
    validate: zodResolver(forgotPasswordSchema),
    validateInputOnBlur: true,
  });

  const request = useMutation({
    mutationFn: requestPasswordReset,
  });

  if (request.isSuccess) {
    return (
      <AuthLayout title="Проверьте почту" subtitle="Письмо уже в пути.">
        <Stack gap="md">
          <Alert color="teal" icon={<IconMailCheck size={17} />} title="Письмо отправлено">
            Если этот адрес нам знаком, на него ушла ссылка для смены пароля.
            Она действует 30 минут и сработает один раз.
          </Alert>
          <Text size="sm" c="var(--text-muted)">
            Письма нет? Загляните в «Спам» и проверьте, тот ли адрес вы указали.
          </Text>
          <Button variant="light" component={Link} to="/login">
            Вернуться ко входу
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Восстановление пароля"
      subtitle="Укажите адрес, на который зарегистрирован аккаунт."
    >
      <form onSubmit={form.onSubmit((values) => request.mutate(values))} noValidate>
        <Stack gap="md">
          {request.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не удалось отправить">
              {toUserMessage(request.error)}
            </Alert>
          ) : null}

          <TextInput
            label="Email"
            placeholder="your@email.ru"
            autoComplete="email"
            required
            {...form.getInputProps('email')}
          />

          <Button type="submit" loading={request.isPending} fullWidth>
            Отправить ссылку
          </Button>
        </Stack>
      </form>

      <Text size="sm" c="var(--text-muted)" ta="center" mt="md">
        Вспомнили пароль?{' '}
        <Anchor component={Link} to="/login">
          Войти
        </Anchor>
      </Text>
    </AuthLayout>
  );
}
